import { Typography, emptyFragment } from '@execonline-inc/execonline-ui';
import { assertNever } from '@kofno/piper';
import { observer } from 'mobx-react';
import { useLayoutEffect, useRef } from 'react';
import { useAutoScroll } from '../../AutoScrollContext';
import ChatDisplayStream from './Display';
import Loading from './Loading';
import { autoScrollIfNeeded } from '../../utils';
import { T } from '../../../../../Translations';
import { ChatStore } from '../../../ChatStores/ChatStore';
import { fromEmpty } from 'maybeasy';

interface Props {
  chatStore: ChatStore;
}

function ChatStream({ chatStore }: Props) {
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const { isAutoScrollEnabled } = useAutoScroll();

  useLayoutEffect(() => {
    autoScrollIfNeeded(isAutoScrollEnabled, messagesEndRef, { behavior: 'smooth', block: 'end' });
  });

  switch (chatStore.state.kind) {
    case 'ready':
      return emptyFragment();
    case 'streaming':
    case 'streaming-and-receiving-input': {
      const { state } = chatStore;
      const { stream } = state;
      return fromEmpty(stream)
        .map(() => <ChatDisplayStream stream={stream} endRef={messagesEndRef} />)
        .getOrElse(() => <Loading chatState={state} />);
    }
    case 'error':
      return (
        <div className="h-full flex items-center justify-center p-4" ref={messagesEndRef}>
          <div className="w-full max-w-lg mx-auto">
            <div className="p-4 bg-white shadow-md rounded-lg border border-gray-300">
              <Typography variant="bodyMedium" as="span">
                <T
                  kind={'I hit a snag generating the collection...'}
                  link={(content) => (
                    <a
                      onClick={chatStore.retryLastMessage}
                      className="cursor-pointer underline"
                      role="button"
                    >
                      {content}
                    </a>
                  )}
                />
              </Typography>
            </div>
          </div>
        </div>
      );
    case 'disconnected':
      return emptyFragment();
    default:
      assertNever(chatStore.state);
  }
}

export default observer(ChatStream);
